import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import Input from '../../../components/Input';

function AddSdkModal({ open, onClose, onConfirm, sdkData = null, editMode = false, loading = false, initData = {} }) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        version: '',
        description: '',
        methodId: '',
        securityId: '',
        status: 0,
    });
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState({});

    // Populate form data when in edit mode
    useEffect(() => {
        if (editMode && sdkData) {
            setFormData({
                version: sdkData.version || '',
                description: sdkData.description || '',
                methodId: sdkData.methodId ?? '',
                securityId: sdkData.securityId ?? '',
                status: sdkData.status ?? 0,
            });
        } else {
            setFormData({
                version: '',
                description: '',
                methodId: '',
                securityId: '',
                status: 0,
            });
        }
        setFile(null);
        setErrors({});
    }, [editMode, sdkData, open]);

    // Handle input change
    const handleChange = (field) => (event) => {
        const value = event.target.value;
        setFormData({
            ...formData,
            [field]: value,
        });
    };

    // Handle file change
    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    // Handle confirm
    const handleConfirm = () => {
        const newErrors = {};

        if (!formData.version.trim()) {
            newErrors.version = t('vneid.sdk.modal.versionRequired');
        }
        if (formData.methodId === '') {
            newErrors.methodId = t('vneid.sdk.modal.methodRequired');
        }
        if (formData.securityId === '') {
            newErrors.securityId = t('vneid.sdk.modal.securityRequired');
        }
        if (!editMode && !file) {
            newErrors.file = t('vneid.sdk.modal.fileRequired');
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Create FormData for multipart/form-data
        const submitData = new FormData();
        submitData.append('Version', formData.version);
        submitData.append('Description', formData.description || '');
        submitData.append('MethodId', formData.methodId);
        submitData.append('SecurityId', formData.securityId);
        submitData.append('Status', formData.status);
        if (file) {
            submitData.append('File', file);
        }

        if (editMode && sdkData) {
            onConfirm({ id: sdkData.id, formData: submitData });
        } else {
            onConfirm(submitData);
        }
        handleClose();
    };

    // Handle close and reset form
    const handleClose = () => {
        setFormData({
            version: '',
            description: '',
            methodId: '',
            securityId: '',
            status: 0,
        });
        setFile(null);
        setErrors({});
        onClose();
    };

    // Check if form is valid
    const isFormValid = () => {
        const fileValid = editMode || file !== null;
        return formData.version.trim() !== '' && formData.methodId !== '' && formData.securityId !== '' && fileValid;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            id="sdk-modal"
            data-testid="sdk-modal"
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {editMode ? <EditIcon /> : <AddIcon />}
                    {editMode ? t('vneid.sdk.modal.editTitle') : t('vneid.sdk.modal.addTitle')}
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {/* Version */}
                    <Input
                        label={t('vneid.sdk.modal.version')}
                        value={formData.version}
                        onChange={handleChange('version')}
                        placeholder={t('vneid.sdk.modal.versionPlaceholder')}
                        required
                        fullWidth
                        error={!!errors.version}
                        helperText={errors.version}
                        id="version"
                        name="version"
                        data-testid="sdk-modal-input-version"
                    />

                    {/* Description */}
                    <Input
                        label={t('vneid.sdk.modal.description')}
                        value={formData.description}
                        onChange={handleChange('description')}
                        placeholder={t('vneid.sdk.modal.descriptionPlaceholder')}
                        fullWidth
                        multiline
                        rows={3}
                        id="description"
                        name="description"
                        data-testid="sdk-modal-input-description"
                    />

                    {/* Method */}
                    <FormControl fullWidth required error={!!errors.methodId}>
                        <InputLabel id="method-label">{t('vneid.sdk.modal.method')}</InputLabel>
                        <Select
                            labelId="method-label"
                            value={formData.methodId}
                            onChange={(e) => setFormData({ ...formData, methodId: e.target.value })}
                            label={t('vneid.sdk.modal.method')}
                            id="methodId"
                            name="methodId"
                            data-testid="sdk-modal-select-methodId"
                        >
                            {(initData.methods || []).map((method) => (
                                <MenuItem key={method.id} value={method.id}>
                                    {method.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Security */}
                    <FormControl fullWidth required error={!!errors.securityId}>
                        <InputLabel id="security-label">{t('vneid.sdk.modal.security')}</InputLabel>
                        <Select
                            labelId="security-label"
                            value={formData.securityId}
                            onChange={(e) => setFormData({ ...formData, securityId: e.target.value })}
                            label={t('vneid.sdk.modal.security')}
                            id="securityId"
                            name="securityId"
                            data-testid="sdk-modal-select-securityId"
                        >
                            {(initData.securities || []).map((security) => (
                                <MenuItem key={security.id} value={security.id}>
                                    {security.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Status */}
                    <FormControl fullWidth>
                        <InputLabel id="status-label">{t('vneid.sdk.modal.status')}</InputLabel>
                        <Select
                            labelId="status-label"
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            label={t('vneid.sdk.modal.status')}
                            id="status"
                            name="status"
                            data-testid="sdk-modal-select-status"
                        >
                            {(initData.statuses || []).map((status) => (
                                <MenuItem key={status.id} value={status.id}>
                                    {status.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* File Upload */}
                    <Box>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{ py: 1.5, borderStyle: errors.file ? 'solid' : 'dashed', borderColor: errors.file ? 'error.main' : 'grey.400' }}
                        >
                            {file ? file.name : t('vneid.sdk.modal.chooseFile')}
                            <input
                                type="file"
                                hidden
                                onChange={handleFileChange}
                                id="file"
                                name="file"
                                data-testid="sdk-modal-input-file"
                            />
                        </Button>
                        {errors.file && (
                            <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>
                                {errors.file}
                            </Box>
                        )}
                        {editMode && !file && sdkData?.fileName && (
                            <Box sx={{ color: 'text.secondary', fontSize: '0.875rem', mt: 1 }}>
                                {t('vneid.sdk.modal.currentFile')}: {sdkData.fileName}
                            </Box>
                        )}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    id="cancel"
                    name="cancel"
                    data-testid="sdk-modal-button-cancel"
                >
                    {t('vneid.sdk.modal.cancel')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={!isFormValid() || loading}
                    id="confirm"
                    name="confirm"
                    data-testid="sdk-modal-button-confirm"
                >
                    {loading ? '...' : t('vneid.sdk.modal.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

AddSdkModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    sdkData: PropTypes.object,
    editMode: PropTypes.bool,
    loading: PropTypes.bool,
    initData: PropTypes.object,
};

export default AddSdkModal;
